import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import { tap } from 'rxjs';
import { LearningSectionLayoutActions } from './learning-section-layout.actions';
import { LearningSectionLayoutJsonModel } from '../../learning-section-layout/learning-section-layout.json.model';
import { LearningSectionLayoutService } from '../../learning-section-layout/learning-section-layout.service';

export interface LearningSectionLayoutStateModel {
  learningSectionLayouts: LearningSectionLayoutJsonModel[]
}

@State<LearningSectionLayoutStateModel>({
  name: 'learningSectionLayout',
  defaults: {
    learningSectionLayouts: []
  }
})
@Injectable()
export class LearningSectionLayoutState {
  private readonly learningSectionLayoutService = inject(LearningSectionLayoutService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: LearningSectionLayoutStateModel) {
    return state;
  }

  @Selector()
  static getLearningSectionLayouts(state: LearningSectionLayoutStateModel) {
    return state.learningSectionLayouts;
  }

  @Selector()
  static getAmountOfLearningSectionLayouts(state: LearningSectionLayoutStateModel) {
    return state.learningSectionLayouts?.length ?? 0;
  }

  @Selector()
  static learningSectionLayoutsIsEmpty(state: LearningSectionLayoutStateModel) {
    return (state.learningSectionLayouts?.length ?? 0) <= 0;
  }

  @Action(LearningSectionLayoutActions.Fetch)
  onFetchLearningSectionLayouts(ctx: StateContext<LearningSectionLayoutStateModel>, action: LearningSectionLayoutActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(
      state => state.fetchOptions
    );

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'name'
    };

    return this.learningSectionLayoutService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new LearningSectionLayoutActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(LearningSectionLayoutActions.FetchSuccess)
  onFetchLearningSectionLayoutsSuccess(ctx: StateContext<LearningSectionLayoutStateModel>, { payload }: LearningSectionLayoutActions.FetchSuccess) {
    ctx.setState({ learningSectionLayouts: payload });
  }

  @Action(LearningSectionLayoutActions.FetchAppend)
  onFetchAppendLearningSectionLayouts(ctx: StateContext<LearningSectionLayoutStateModel>) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);
    return this.learningSectionLayoutService.getAllWithPagination({}, localFetchOptions).pipe(
      tap(res => {
        ctx.dispatch(new LearningSectionLayoutActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(LearningSectionLayoutActions.FetchSuccessAppend)
  onFetchLearningSectionLayoutsSuccessAppend(ctx: StateContext<LearningSectionLayoutStateModel>, { payload }: LearningSectionLayoutActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      learningSectionLayouts: [...state.learningSectionLayouts, ...payload]
    });
  }

  @Action(LearningSectionLayoutActions.Delete)
  onDelete(ctx: StateContext<LearningSectionLayoutJsonModel>, { payload }: LearningSectionLayoutActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Layout',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new LearningSectionLayoutActions.Fetch());
        })
      );
  }
}
